import AuthAPI from "../apis/AuthAPI";
import AlertService from "../services/AlertService";
import store from "../store";
import { API_ROOT, awaitableRootSchoolCode } from "../apis/ApiConfig";

class AuthService {
  NonStudentRoles = [
    "SuperAdmin",
    "Admin",
    "Manager",
    "ReadOnly",
    "Member",
    "Verifier",
    "Reviewer",
    "CorporateVerifier",
    "CorporateReviewer",
  ];

  async isAuthenticated() {
    try {
      const resp = await AuthAPI.get("/sso/isAuthenticated");
      return resp.data;
    } catch (err) {
      console.log(err);
    }
  }

  async passedMFA() {
    try {
      const resp = await AuthAPI.get("/auth/passedMFA");
      return resp.data;
    } catch (err) {
      console.log(err);
    }
  }

  async updateToken(updateRequest) {
    // Update request object
    // {claim: "name of claim to be updated", newValue: "value the existing token claim will be replaced with"}
    // Claim options (ClaimsEnum.cs):
    //    SCHOOL_CODE, ROLE, USERNAME, MASTER_USER_PROFILE_ID, ACCOUNT_ID, UUID
    try {
      await AuthAPI.post(`/sso/updateToken/`, updateRequest);
    } catch (err) {
      console.log(err);
    }
  }

  async ssoLogin(email, password) {
    try {
      const resp = await AuthAPI.post("/sso/ssologin", {
        email: "",
        password: password,
      });
      return resp.data;
    } catch (err) {
      console.log(err);
    }
  }

  async getMasterUserProfiles() {
    try {
      let response = await AuthAPI.get("/sso/MasterUserProfile");
      return response.data;
    } catch (err) {
      AlertService.addErrorAlert(err?.response?.data ?? "Failed to get MasterUserProfile");
    }
  }

  async ssoProfileLogin(profile) {
    try {
      let response = await AuthAPI.post("/sso/MasterUserProfileLogin", profile);
      return response.data;
    } catch (err) {
      console.log(err);
    }
  }

  async getCurrentSsoUser() {
    try {
      let response = await AuthAPI.get("/sso/getCurrentSsoUser");
      return response.data;
    } catch (err) {
      console.log(err);
    }
  }

  async getUser() {
    try {
      let response = await AuthAPI.get("/auth/getUser");
      return response.data;
    } catch (err) {
      console.log(err);
    }
  }

  async studentLookup(lookupDto) {
    try {
      const response = await AuthAPI.post("/auth/studentLookup", lookupDto);
      return response.data;
    } catch (err) {
      console.log(err);
    }
  }

  async studentAccountLookup(lookupDto) {
    try {
      const response = await AuthAPI.post("/auth/StudentAccountLookup", {
        Username: lookupDto.Username,
        SchoolCode: await lookupDto.SchoolCode,
        PURL: lookupDto.PURL,
      });
      return response.data;
    } catch (err) {}
  }

  async userLogin(username, password, noPasswordCallback) {
    try {
      const response = await AuthAPI.post("/auth/userLogin", {
        Username: username,
        Password: password,
        SchoolCode: await awaitableRootSchoolCode(),
      });
      return response.data;
    } catch (err) {
      if(err.response.status === 403 && err.response.data && noPasswordCallback) {
        noPasswordCallback(err.response.data);
      } else {
        AlertService.addErrorAlert(err.response.data);
      }
    }
  }

  async logout() {
    const response = await AuthAPI.post("/sso/ssoLogout");
    return response.data;
  }

  async verifyCredentials(username, password) {
    try {
      const response = await AuthAPI.post("/auth/verifyCredentials", {
        Username: username,
        Password: password,
        SchoolCode: store.getState().school.schoolCode,
        PURL: API_ROOT,
      });
      return response.data;
    } catch (err) {
      AlertService.addErrorAlert(err.response.data);
    }
  }

  async checkResetAuthentication(uuid, token, accountType) {
    try {
      const response = await AuthAPI.post("/auth/checkResetAuthentication", {
        Uuid: uuid,
        PasswordToken: token,
        SchoolCode: await awaitableRootSchoolCode(),
        AccountType: accountType,
      });
      return response.data;
    } catch (err) {}
  }

  async updatePasswordForReset(uuid, passwordToken, accountType, password) {
    try {
      const response = await AuthAPI.post("/auth/UpdatePasswordForReset", {
        Uuid: uuid,
        SchoolCode: await awaitableRootSchoolCode(),
        PasswordToken: passwordToken,
        AccountType: accountType,
        Password: password,
      });
      return response.data;
    } catch (err) {
      AlertService.addErrorAlert(err.response.data);
    }
  }

  async SearchNotification(parameters) {
    try {
      const resp = await AuthAPI.post("/Notification/search", parameters);
      return resp.data;
    } catch (err) {
      console.log(err);
      return;
    }
  }

  async getSchoolCodeFromURL(url) {
    try {
      const resp = await AuthAPI.get(`/auth/getSchoolCodeFromURL/${url}`);
      return resp.data;
    } catch (err) {
      console.log(err);
    }
  }

  async getSchoolFile(schoolCode, fileType) {
    try {
      const resp = await AuthAPI.post("/auth/fileDownload", {
        schoolCode: schoolCode,
        fileType: fileType
      });

      return resp.data.downloadResult;
    } catch (err) {
      return null;
    }
  }

  async getSchoolDataForLogin(schoolCode) {
    try {
      const resp = await AuthAPI.get(`/auth/getSchoolDataForLogin/${schoolCode}`);

      return resp.data;
    } catch (err) {
      return null;
    }
  }

  async sendMFACode(recipient, messageType) {
    try {
      const resp = await AuthAPI.post("/auth/sendMFACode", {
        recipient : recipient,
        messageType : messageType
      });

      return resp.data;
    } catch (err) {
      return { "success": false, "message": err?.response?.data ?? err.message };
    }
  }

  async checkMFACode(recipient, messageType, code) {
    try {
      const resp = await AuthAPI.post("/auth/checkMFACode", {
        recipient : recipient,
        messageType : messageType,
        code: code
      });

      return resp.data;
    } catch (err) {
      return { "success": false, "message": err?.response?.data ?? err.message };
    }
  }

  async getBetaTestToggleConfig(key) {
    try {
      const resp = await AuthAPI.get("/auth/getBetaTestToggleConfig/" + key);
      return resp.data;
    } catch (err) {
      AlertService.addErrorAlert("Could not retrieve " + key);
      console.log(key + " was not found");
      return "";
    }
  }

  downloadLinkInNewTab(alfrescoID, fileID, fileName, studentID, documentType, interviewTypeID, versionID) {
    if (fileID.includes("workspace")) {
      let parts = fileID.split("/");
      fileID = parts[parts.length -1];
    }
    if(fileID.includes(";")) {
      fileID = fileID.split(";")[0];
    }
    let url = `${API_ROOT}/api/auth/fileDownload/${alfrescoID}/${btoa(fileID)}/${documentType}?fileName=${encodeURIComponent(fileName)}`;
    if(studentID)
      url = url + `&studentID=${studentID}`;
    if(interviewTypeID)
      url = url = `%interviewTypeID=${interviewTypeID}`;
    if(versionID)
      url = url + `&versionID=${versionID}`;
    window.open(url, '_blank');
  }
}

let instance = new AuthService();
export default instance;
